/**
 * A product list with filters: remembers the list or tile view, applies the
 * sort order as soon as it is chosen, loads the next page in place, and
 * shortens long filter lists to their first values with a link to show all.
 * Without this script the same controls work as plain links and a form.
 */
const VIEW_KEY = 'sda-view';
const SHORT_LIST = 6;

class ProductListing extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.#showView(this.#storedView());
    this.#shortenFilters();
  }

  /** Checked values stay in view, so a shortened list never hides a choice that is in effect. */
  #shortenFilters() {
    for (const list of this.querySelectorAll('.sda-checks[data-more]')) {
      if (list.children.length <= SHORT_LIST + 2) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sda-link sda-checks__more';
      button.setAttribute('aria-expanded', 'false');
      button.textContent = list.dataset.more;
      list.classList.add('is-short');
      list.append(button);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #storedView() {
    try {
      return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  }

  #showView(view) {
    this.querySelector('[data-listing-grid]')?.classList.toggle('is-list', view === 'list');
    for (const button of this.querySelectorAll('[data-view]')) {
      button.setAttribute('aria-pressed', String(button.dataset.view === view));
    }
  }

  #onClick = (event) => {
    const viewButton = event.target.closest('[data-view]');
    if (viewButton) {
      this.#showView(viewButton.dataset.view);
      try {
        localStorage.setItem(VIEW_KEY, viewButton.dataset.view);
      } catch {
        // The view still switches; it is just not remembered.
      }
      return;
    }

    const more = event.target.closest('.sda-checks__more');
    if (more) {
      const list = more.parentElement;
      const short = list.classList.toggle('is-short');
      more.setAttribute('aria-expanded', String(!short));
      more.textContent = short ? list.dataset.more : list.dataset.less;
      return;
    }

    const next = event.target.closest('[data-listing-next]');
    if (next && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      this.#loadMore(next);
    }
  };

  #onChange = (event) => {
    if (event.target.name === 'sort_by') event.target.form?.requestSubmit();
  };

  async #loadMore(link) {
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(link.href, location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Listing request failed: ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = page.querySelector('[data-listing-grid]')?.children ?? [];
      const grid = this.querySelector('[data-listing-grid]');
      const first = cards[0];
      grid.append(...cards);
      this.querySelector('[data-listing-more]').replaceWith(page.querySelector('[data-listing-more]') ?? '');

      history.replaceState(history.state, '', link.href);
      first?.querySelector('a')?.focus({ preventScroll: true });
    } catch {
      location.assign(link.href);
    }
  }
}

if (!customElements.get('product-listing')) customElements.define('product-listing', ProductListing);
